import { MOBILITY_METRICS } from 'ui/clinicianScreens/Patient/Report/sections/Mobility/consts'
import {
  AVG_PER,
  CHANGE_THRESHOLD_DEFAULT,
  SECTION_TITLES,
} from 'ui/clinicianScreens/Patient/Report/consts'
import {
  formatAverage,
  formatDiff,
  formatInHoursAndMinutes,
  getRoundedPercentChange,
  isAtOrBeyondThreshold,
} from 'ui/clinicianScreens/Patient/Report/helpers'
import {
  formatMobilityDiff,
  getMobilityIconVariant,
} from 'ui/clinicianScreens/Patient/Report/sections/Mobility/helpers'
import { AFTER, BEFORE } from 'utilities/sort'
import { getAverageDiff } from '../TremorAndDyskinesia/helpers'

export const getHighlightDefaultText = (
  t,
  percentChange,
  trendDiff,
  currentAvg,
  prevAvg,
) => {
  if (currentAvg && !prevAvg) return ''
  if (!percentChange) return t('report.common.noChange')
  return `${Math.abs(percentChange)}% (${trendDiff})`
}

export const getEmergencyDepartmentVisitHighlight = ({
  t,
  currentCount = 0,
  prevCount = 0,
  metricCategory,
}) => {
  const trend = currentCount - prevCount
  const iconVariant = trend > 0 ? 'worse' : trend < 0 ? 'better' : null

  return {
    metric: metricCategory,
    value: currentCount,
    trend,
    iconVariant,
    notes: t('report.highlights.previousPeriodCount', { count: prevCount }),
  }
}

export const getMobilityHighlight = ({
  t,
  currentData,
  prevData,
  metricCategory,
  subtitle,
  mobilityMetricId,
  invertColors,
  noteSuffix = '',
}) => {
  if (!currentData?.averageValue)
    return {
      trend: 0,
    }

  const value = `${currentData?.averageValue}${currentData?.averageUnit}`
  const percentChange = getRoundedPercentChange(
    currentData?.averageValue,
    prevData?.averageValue,
  )

  if (!isAtOrBeyondThreshold(percentChange)) return { trend: 0 }

  const iconVariant = getMobilityIconVariant({
    trend: percentChange,
    invertColors,
  })
  const trendLabel = formatMobilityDiff(t, currentData, prevData)

  return {
    metric: metricCategory,
    subtitle,
    mobilityMetricId,
    value,
    trend: percentChange,
    iconVariant,
    notes: percentChange
      ? `${Math.abs(percentChange)}% (${trendLabel})${noteSuffix}`
      : `${t('report.highlights.noSignificantChange')}${noteSuffix}`,
  }
}

export const getTremorDyskinesiaHighlight = ({
  t,
  currentData,
  prevData,
  metricCategory,
  percentChange,
  subtitle = AVG_PER.day,
}) => {
  const currentAverage = formatAverage(currentData)
  const prevAverage = formatAverage(prevData)
  const getTDIconVariant = () => {
    if (!isAtOrBeyondThreshold(percentChange)) {
      return null
    }
    return percentChange < CHANGE_THRESHOLD_DEFAULT ? 'better' : 'worse'
  }
  const avgDiff = getAverageDiff(currentAverage, prevAverage)
  const trendDiff = formatDiff(t, avgDiff)

  return {
    metric: metricCategory,
    subtitle,
    value: formatInHoursAndMinutes(currentAverage),
    trend: percentChange,
    iconVariant: getTDIconVariant(),
    notes: getHighlightDefaultText(
      t,
      percentChange,
      trendDiff,
      currentAverage,
      prevAverage,
    ),
  }
}

export const sortHighlights = (highlights) => {
  if (!highlights) return []
  const { EMERGENCY_DEPT_VISIT, TREMOR, DYSKINESIA, ACTIVITY, MOBILITY } =
    SECTION_TITLES
  const { doubleSupport, stepLength, walkingSpeed } = MOBILITY_METRICS

  const highlightsOrder = [
    EMERGENCY_DEPT_VISIT,
    TREMOR,
    DYSKINESIA,
    ACTIVITY,
    stepLength.id,
    walkingSpeed.id,
    doubleSupport.id,
    MOBILITY,
  ]

  const getItemFromHighlightsOrder = (highlight) => {
    const { metric, mobilityMetricId } = highlight

    if (metric === MOBILITY && mobilityMetricId) {
      return mobilityMetricId
    }
    return metric
  }

  return highlights.sort((a, b) => {
    if (
      highlightsOrder.indexOf(getItemFromHighlightsOrder(a)) <
      highlightsOrder.indexOf(getItemFromHighlightsOrder(b))
    ) {
      return AFTER
    } else {
      return BEFORE
    }
  })
}
